const post = {
  meta: {
    slug: "time-in-business-funding-options",
    title: "How Time-in-Business Really Affects Your Funding Options",
    excerpt:
      "Six months, one year, two years, five years — each threshold unlocks a different tier of capital. Here's exactly what changes at each door.",
    date: "2025-12-24",
    readTime: "5 min read",
    category: "Strategy",
    image:
      "https://images.pexels.com/photos/33866469/pexels-photo-33866469.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  body: `
## Why lenders care so much about age

Time in business is the single strongest predictor of loan default. A business that's survived three years has already beaten the mortality curve. A business that's four months old has not.

Lenders don't measure loyalty or hustle. They measure **months of operating history they can read in your bank statements** — and they price the risk accordingly.

Here's what unlocks at each door.

## 0–6 months: startup territory

**What's available:**
- Personal credit cards (business cards require a personal guarantee anyway)
- Home equity line of credit (HELOC)
- Friends-and-family capital
- Startup equipment financing (specific to some equipment vendors)
- Very limited MCA (rare, expensive, small amounts)

**What isn't:**
- SBA loans (require 2+ years of returns in most cases)
- Bank lines of credit
- Term loans from traditional lenders
- Most fintech term loans and lines

**Reality check:** at this stage, "business financing" is largely a myth for most operators. Your personal credit is what's actually being lent against. Focus on generating revenue and building your bank statement history before pursuing business capital.

## 6–12 months: the first real door

At the six-month mark, you enter revenue-based financing territory.

**Newly available:**
- **Merchant cash advance** — most funders will look at your file with 6+ months of statements
- **Some fintech term loans** — starting at 6–9 months in business, lower amounts
- **Equipment financing** — vendor-backed or startup-focused programs
- **Business credit cards with higher limits** (still personally guaranteed)

**Still not available:**
- SBA loans (except SBA microloans, up to $50K, community-based)
- Traditional bank capital of any kind
- Prime fintech lines of credit

**What matters most:** consistent monthly deposits. A business with 8 months and $50K/month in clean deposits will out-borrow a business with 18 months and $12K/month.

## 12–24 months: the middle door

The 12-month mark is a real inflection point. This is where most fintech and revenue-based products fully open up.

**Newly available:**
- **Fintech term loans** up to $250K
- **Fintech lines of credit** up to $150K (typically at 12+ months and 660+ FICO)
- **Higher MCA amounts** (up to $500K depending on revenue)
- **Full equipment financing** through most vendors and independent lenders
- **Some SBA microloans and SBA Express** ($50K–$150K, faster process)

**Still limited:**
- Full SBA 7(a) and 504 typically require 2+ years and profitability on tax returns
- Bank commercial lines of credit (banks usually want 2+ years)

**What matters most at this stage:** the trajectory. Are deposits growing? Is the average balance improving? Is credit score above 650? Consistency across all three metrics starts to unlock real bank interest around month 18.

## 24 months+: the SBA door opens

Two years is the magic number for most SBA products. You need:
- Two complete years of business tax returns filed
- 24+ months of operating bank statements
- Demonstrable revenue and, ideally, profitability

**Newly available:**
- **SBA 7(a) loans up to $5M** — with the right file, this is the cheapest capital available
- **SBA 504 loans for real estate/equipment** up to $5.5M
- **Bank commercial lines of credit** (typically Prime + 1.5–3.5%)
- **Bank term loans** at bank rates (rather than fintech rates)
- **Larger fintech term loans** (up to $1M+) at the best pricing tiers

**The tax return quirk:** the SBA wants your *most recent* filed year. If it's June 2026 and you filed 2024 but not yet 2025, you're in the awkward window where some lenders want the 2025 extension filed before they'll underwrite. Filing your business return promptly each spring is a free way to keep your options open.

## 3+ years: full market access

At three years and beyond, if your credit and revenue support it, essentially every business finance product in the market is open to you. This is where you start to see:

- **Bank preferred pricing** — Prime + 0.5–1.5% on the strongest files
- **Unsecured lines of credit** at meaningful sizes
- **Multi-year term loans** at full commercial rates
- **Complex structured deals** — acquisitions, buyouts, expansion financing
- **Portfolio underwriting** — some banks will underwrite a relationship instead of a single loan

## The two mistakes new businesses make

**Mistake #1: Applying too early for the wrong products.** Applying for a bank line of credit at month 8 is a decline waiting to happen. Each decline can put a hard pull on your credit and burn a lender relationship for future rounds. Wait until you actually qualify.

**Mistake #2: Ignoring the 24-month runway.** If you know you'll want SBA capital eventually, prepare for it *now*, not at month 23. That means: file every tax return promptly, keep clean bank statements, avoid stacking MCAs (which will disqualify you from SBA underwriting later), and grow your business credit file with vendor tradelines.

## The broker's cheat sheet

| Months in Business | What's realistically available |
|---|---|
| 0–6 | Personal credit + HELOC + vendor equipment |
| 6–12 | MCA, some fintech term, equipment financing |
| 12–18 | Full fintech suite, higher MCA, SBA microloan |
| 18–24 | Prime fintech pricing, SBA Express |
| 24+ | Full SBA 7(a)/504, bank LOC, bank term |
| 36+ | Best bank pricing, complex structured deals |

**The takeaway:** every month in business quietly upgrades your borrowing tier. Time is the one variable in finance that you can't accelerate but can absolutely waste. Use the early months to build clean statements, avoid stacking bad debt, and prepare the file that year two will actually be able to use.
`,
};

export default post;
